import { FC, useCallback, useEffect, useMemo, useRef } from "react";

import { DEFAULT_CHEQUE_MINIMUM_AMOUNT, FormType, getField } from "@simplyk/common";
import { useForm } from "react-hook-form";

import { trpc } from "../../../../../common/helpers/trpc";
import { useTranslate } from "../../../../../common/hooks/useTranslate";
import { STEP_SCROLL_TARGET_MAP } from "../../AnimatePreview/getScrollTopStepTarget";
import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";
import { FormStepContainer } from "../FormStepContainer";
import { useFormSaveAndNavigate } from "../useFormSaveAndNavigate";

import { AdvancedOptions } from "./AdvancedOptions/AdvancedOptions";
import { joinNotificationEmails, parseNotificationEmails } from "./helpers";

import { useUpdateDonationDataInPreview } from "@/features/LiveFormEditor/common/useUpdateLiveTicketingPreview/useUpdateDonationDataInPreview";
import { StepAdvancedSettingsFormData } from "@/features/LiveFormEditor/Steps/StepAdvancedSettings/types";

export const DonationLiveEditorStepAdvancedSettings: FC = () => {
  const { t } = useTranslate();
  const utils = trpc.useUtils();
  const { apiFormObject: apiDonationForm } = useLiveFormEditorContext<"donation-form">();

  const organizationId = apiDonationForm.organizationId ?? apiDonationForm.organization?.id ?? "";
  const { data: organizationPageInfo, isLoading: isOrganizationPageInfoLoading } =
    trpc.getOrganizationPageInfo.useQuery(
      { organizationId },
      { enabled: Boolean(organizationId), refetchOnMount: "always" }
    );

  const donationFormField = getField(apiDonationForm.donationFormFields, apiDonationForm.locale);

  const { data: formFundsResult } = trpc.getFormFunds.useQuery(
    {
      formId: apiDonationForm.id,
      formType: FormType.DonationForm,
      locale: apiDonationForm.locale,
    },
    { enabled: Boolean(apiDonationForm.id) }
  );

  const hasOrgPage = Boolean(organizationPageInfo?.isPublished === true);
  const supportFormsCount = organizationPageInfo?.supportForms?.length ?? 0;
  const isSupportFormOnOrgPage = organizationPageInfo?.supportForms?.some((sf) => sf.id === apiDonationForm.id);
  const isDraftWithRoomOnOrgPage = hasOrgPage && apiDonationForm.status === "draft" && supportFormsCount < 3;

  const formValues = useMemo<StepAdvancedSettingsFormData>(() => {
    const hasSingleLinkedFund = formFundsResult?.funds.length === 1;
    const displayOnOrgPageSupportForm = hasOrgPage
      ? isDraftWithRoomOnOrgPage || (isSupportFormOnOrgPage ?? false)
      : undefined;

    return {
      allowInHonour: apiDonationForm.allowInHonour || false,
      dafEnabled: apiDonationForm.dafEnabled ?? false,
      chequeEnabled: apiDonationForm.chequeMinimumAmount !== null,
      chequeMinimumAmount: apiDonationForm.chequeMinimumAmount ?? undefined,
      chequeDescription: donationFormField?.chequeDescription || "",
      displayThermometer: false,
      thermometerTarget: undefined,
      displayDonorList: apiDonationForm.displayDonorList,
      fundDesignationEnabled: hasSingleLinkedFund,
      selectedFundId: hasSingleLinkedFund ? formFundsResult!.funds[0].fundId : null,
      displayOnOrgPageSupportForm,
      notificationEmails: joinNotificationEmails(apiDonationForm.notificationEmails),
    };
  }, [
    apiDonationForm,
    donationFormField?.chequeDescription,
    formFundsResult,
    hasOrgPage,
    isDraftWithRoomOnOrgPage,
    isSupportFormOnOrgPage,
  ]);

  const form = useForm<StepAdvancedSettingsFormData>({
    values: formValues,
    resetOptions: { keepDirtyValues: true },
  });
  const lastChangedFieldRef = useRef<string | undefined>(undefined);
  const previousValuesRef = useRef({
    allowInHonour: formValues.allowInHonour,
    dafEnabled: formValues.dafEnabled,
    chequeEnabled: formValues.chequeEnabled,
    displayDonorList: formValues.displayDonorList,
  });

  const shouldAlwaysSaveForOrgPageInit = Boolean(isDraftWithRoomOnOrgPage && !isSupportFormOnOrgPage);

  // Detect which field changed by comparing current form values against tracked previous values.
  // This runs synchronously inside getScrollContext to avoid race conditions with watch subscription ordering.
  const getScrollContext = useCallback(() => {
    const currentValues = form.getValues();
    const prev = previousValuesRef.current;

    if (currentValues.dafEnabled !== prev.dafEnabled) {
      lastChangedFieldRef.current = "dafEnabled";
      previousValuesRef.current.dafEnabled = currentValues.dafEnabled;
    }
    if (currentValues.chequeEnabled !== prev.chequeEnabled) {
      lastChangedFieldRef.current = "chequeEnabled";
      previousValuesRef.current.chequeEnabled = currentValues.chequeEnabled;
    }
    if (currentValues.allowInHonour !== prev.allowInHonour) {
      lastChangedFieldRef.current = "allowInHonour";
      previousValuesRef.current.allowInHonour = currentValues.allowInHonour;
    }
    if (currentValues.displayDonorList !== prev.displayDonorList) {
      lastChangedFieldRef.current = "displayDonorList";
      previousValuesRef.current.displayDonorList = currentValues.displayDonorList;
    }

    const context = {
      lastChangedField: lastChangedFieldRef.current,
      allowInHonour: currentValues.allowInHonour,
      displayDonorList: currentValues.displayDonorList,
    };
    const resolveTarget = STEP_SCROLL_TARGET_MAP["advanced-settings"];
    return { ...context, targetElementId: typeof resolveTarget === "function" ? resolveTarget(context) : undefined };
  }, [form]);

  const editAdvancedParametersMutation = trpc.updateDonationFormAdvancedSettings.useMutation();
  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepAdvancedSettingsFormData>({
    type: "donation-form",
    form,
    alwaysSaveOnNext: shouldAlwaysSaveForOrgPageInit,
    alwaysSaveOnBack: shouldAlwaysSaveForOrgPageInit,
    saveData: async (formData) => {
      const fundFieldsTouched =
        form.formState.dirtyFields.fundDesignationEnabled || form.formState.dirtyFields.selectedFundId;
      const effectiveDisplayOnOrgPageSupportForm =
        formData.displayOnOrgPageSupportForm ?? (isDraftWithRoomOnOrgPage ? true : undefined);

      const result = await editAdvancedParametersMutation.mutateAsync({
        donationFormId: apiDonationForm.id,
        dafEnabled: formData.dafEnabled,
        chequeMinimumAmount: formData.chequeEnabled
          ? (formData.chequeMinimumAmount ?? DEFAULT_CHEQUE_MINIMUM_AMOUNT)
          : null,
        allowInHonour: formData.allowInHonour,
        ...(form.formState.dirtyFields.notificationEmails && {
          notificationEmails: parseNotificationEmails(formData.notificationEmails),
        }),
        chequeDescription: formData.chequeEnabled ? formData.chequeDescription : null,
        displayDonorList: formData.displayDonorList,
        selectedFundId: fundFieldsTouched
          ? formData.fundDesignationEnabled
            ? formData.selectedFundId
            : null
          : undefined,
        ...(effectiveDisplayOnOrgPageSupportForm !== undefined && {
          supportFormOnOrgPage: {
            isActive: effectiveDisplayOnOrgPageSupportForm,
            ...(formData.replaceFormOnOrgPage && {
              replaceForm: formData.replaceFormOnOrgPage,
            }),
          },
        }),
      });

      if (fundFieldsTouched) {
        void utils.getFormFunds.invalidate({
          formId: apiDonationForm.id,
          formType: FormType.DonationForm,
          locale: apiDonationForm.locale,
        });
      }
      if (hasOrgPage && effectiveDisplayOnOrgPageSupportForm !== undefined) {
        const orgId = apiDonationForm.organizationId ?? apiDonationForm.organization?.id;
        if (orgId) {
          void utils.getOrganizationPageInfo.invalidate({ organizationId: orgId });
        }
      }
      return result;
    },
  });

  // Reset the form when the cheque description translation is updated
  useEffect(() => {
    form.setValue("chequeDescription", donationFormField?.chequeDescription || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [donationFormField?.chequeDescription]);

  useUpdateDonationDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      liveDataToMutate.donation.dafEnabled = formData.dafEnabled ?? false;
      liveDataToMutate.donation.chequeMinimumAmount = formData.chequeEnabled
        ? (formData.chequeMinimumAmount ?? DEFAULT_CHEQUE_MINIMUM_AMOUNT)
        : null;
      liveDataToMutate.donation.allowInHonour = formData.allowInHonour;
      liveDataToMutate.donation.displayDonorList = formData.displayDonorList ?? false;
      // Detect which field changed before reading the ref — ensures it's up-to-date
      // even when this watch fires before useAnimatePreviewOnFormEvents's watch
      const { lastChangedField } = getScrollContext();
      liveDataToMutate.lastChangedAdvancedField = lastChangedField;
      liveDataToMutate.donation.donationFormFields = liveDataToMutate.donation.donationFormFields?.map((field) => {
        return {
          ...field,
          chequeDescription: formData.chequeDescription,
        };
      });
    },
  });

  return (
    <FormStepContainer
      title={t("dashboard", "step.advancedSettings.title")}
      form={form}
      actionsProps={{
        onNext: handleSaveAndNext,
        onBack: handleSaveAndBack,
      }}
      getScrollContext={getScrollContext}
    >
      <AdvancedOptions
        form={form}
        formType={FormType.DonationForm}
        formId={apiDonationForm.id}
        locale={apiDonationForm.locale}
        organizationPageInfo={organizationPageInfo}
        isOrganizationPageInfoLoading={isOrganizationPageInfoLoading}
      />
    </FormStepContainer>
  );
};
